import { useRef, useState } from "react";
import { Upload, AlertTriangle, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_META = {
  new: { label: "New", color: "#10B981" },
  possible_duplicate: { label: "Possible duplicate", color: "#F59E0B" },
  duplicate: { label: "Already imported", color: "#71717A" },
};

export const ImportTradesModal = ({ open, onOpenChange, onImported }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);

  const reset = () => { setPreview(null); setChecked({}); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/trade-log/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const init = {};
      data.rows.forEach((r, i) => { init[i] = r.row_status === "new"; });
      setChecked(init);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse file — expected an IBKR .tlg or trade CSV export");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    const rows = preview.rows.filter((r, i) => checked[i] && r.row_status !== "duplicate");
    if (!rows.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/trade-log/import/commit", { rows, filename: preview.filename });
      if (data.inserted > 0 && data.batch_id) {
        try {
          sessionStorage.setItem("ml-last-import", JSON.stringify({ batch_id: data.batch_id, count: data.inserted, ts: Date.now() }));
        } catch { /* ignore */ }
      }
      const posMsg = data.positions_created
        ? ` · ${data.positions_created} open position${data.positions_created === 1 ? "" : "s"} added to Portfolio`
        : "";
      toast.success(`Imported ${data.inserted} trade${data.inserted === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped as duplicates` : ""}${posMsg}`, {
        description: data.inserted > 0 ? "Mistake? Use “Undo last import” next to the import button." : undefined,
      });
      if (data.portfolio_sync_warning) {
        toast.warning("Portfolio sync check", { description: data.portfolio_sync_warning, duration: 10000 });
      }
      reset();
      onOpenChange(false);
      onImported && onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const selCount = preview ? preview.rows.filter((r, i) => checked[i] && r.row_status !== "duplicate").length : 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-[#111111] border-[#27272A] rounded-sm max-w-3xl" data-testid="import-trades-modal">
        <DialogHeader><DialogTitle className="text-sm">Import broker trade log</DialogTitle></DialogHeader>
        {!preview ? (
          <div className="space-y-3">
            <p className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
              Upload a broker TradeLog or trade CSV, including supported IBKR formats such as <b>TradeLog (.tlg)</b>,
              <b> Activity Statement</b>, or <b>Flex Query</b> exports. The file is processed only for import, and
              account identifiers, holder names, and uploaded filenames are not stored. Imports are always added as
              <b> new</b> entries; open positions found in the file are added to your Portfolio automatically, and
              existing manually entered positions are never modified.
            </p>
            <input ref={fileRef} type="file" accept=".csv,.tlg,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="import-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={busy}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-9 text-xs" data-testid="import-choose-file-btn">
              {busy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              {busy ? "Parsing…" : "Choose file (.tlg / .csv)"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-[10px] flex-wrap" data-testid="import-summary">
              <span className="flex items-center gap-1" style={{ color: "#10B981" }}><CheckCircle className="w-3 h-3" />{preview.summary.new} new</span>
              <span className="flex items-center gap-1" style={{ color: "#F59E0B" }}><AlertTriangle className="w-3 h-3" />{preview.summary.possible_duplicate} possible duplicates</span>
              <span className="flex items-center gap-1" style={{ color: "#71717A" }}><XCircle className="w-3 h-3" />{preview.summary.duplicate} already imported</span>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>· {preview.filename}</span>
            </div>
            <div className="border rounded-sm max-h-72 overflow-y-auto" style={{ borderColor: "var(--hoip-border)" }}>
              <table className="w-full dense-table" data-testid="import-review-table">
                <thead className="sticky top-0" style={{ background: "#111111" }}>
                  <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                    {["", "Ticker", "Type", "Strike", "Expiry", "Qty", "Price", "P&L", "Date", "Status", "Flag"].map(h => (
                      <th key={h} className="text-left text-[10px] font-medium px-1.5 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const meta = STATUS_META[r.row_status];
                    const disabled = r.row_status === "duplicate";
                    return (
                      <tr key={i} className="border-b" style={{ borderColor: "var(--hoip-divider)", opacity: disabled ? 0.45 : 1 }} data-testid={`import-row-${i}`}>
                        <td className="px-1.5"><input type="checkbox" disabled={disabled} checked={!!checked[i] && !disabled}
                          onChange={(e) => setChecked({ ...checked, [i]: e.target.checked })} data-testid={`import-check-${i}`} /></td>
                        <td className="text-xs font-mono-data text-blue-400 px-1.5">{r.ticker}</td>
                        <td className="text-[10px] px-1.5" style={{ color: "var(--hoip-text-secondary)" }}>{r.strategy?.replace(/_/g, " ")}</td>
                        <td className="text-xs font-mono-data px-1.5">{r.strike ? `$${r.strike}` : "—"}</td>
                        <td className="text-[10px] font-mono-data px-1.5">{r.expiry || "—"}</td>
                        <td className="text-xs font-mono-data px-1.5">{r.qty}</td>
                        <td className="text-xs font-mono-data px-1.5">{r.price != null ? `$${r.price}` : "—"}</td>
                        <td className={`text-xs font-mono-data px-1.5 ${r.pnl > 0 ? "text-emerald-500" : r.pnl < 0 ? "text-red-500" : ""}`}>{r.pnl != null ? `$${r.pnl.toFixed(2)}` : "—"}</td>
                        <td className="text-[10px] font-mono-data px-1.5">{r.date || "—"}</td>
                        <td className="text-[10px] px-1.5">{r.status}</td>
                        <td className="px-1.5">
                          <span className="text-[9px] px-1 py-0.5 rounded-sm font-medium" title={r.reason || ""}
                            style={{ color: meta.color, background: `${meta.color}15` }}>{meta.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[9px] italic" style={{ color: "var(--hoip-text-tertiary)" }}>
              Possible duplicates share a ticker/strike/date with an existing entry but have a different execution ID —
              include them only if you know they are distinct fills. Nothing is ever auto-merged.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} className="rounded-sm h-8 text-xs flex-1 border-[#27272A]" data-testid="import-back-btn">Choose another file</Button>
              <Button onClick={commit} disabled={busy || selCount === 0}
                className="rounded-sm h-8 text-xs flex-1 bg-white text-black hover:bg-gray-200" data-testid="import-commit-btn">
                {busy ? "Importing…" : `Import ${selCount} trade${selCount === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
